import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { formatDate, getRoleName, cn } from '../../lib/utils';
import { Plus, User, Mail, Phone, Shield, Search } from 'lucide-react';
import { toast } from 'sonner';

const UsersPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'worker',
    phone: '',
    assigned_houses: [],
    linked_participants: []
  });

  useEffect(() => {
    fetchData();
  }, [roleFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = roleFilter !== 'all' ? { role: roleFilter } : {};
      const [usersRes, housesRes, participantsRes] = await Promise.all([
        api.get('/users', { params }),
        api.get('/houses'),
        api.get('/participants')
      ]);
      setUsers(usersRes.data);
      setHouses(housesRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/auth/register', newUser);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'worker',
        phone: '',
        assigned_houses: [],
        linked_participants: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const filteredUsers = users.filter(u => 
    `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
      house_manager: 'bg-teal-500/10 text-teal-500 border-teal-500/30',
      worker: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
      family: 'bg-pink-500/10 text-pink-500 border-pink-500/30'
    };
    return classes[role] || '';
  };

  const columns = [
    {
      header: 'User',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
            {row.first_name[0]}{row.last_name[0]}
          </div>
          <div>
            <p className="font-medium">{row.first_name} {row.last_name}</p>
            <p className="text-xs text-muted-foreground">{row.email}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Role',
      cell: (row) => (
        <Badge variant="outline" className={cn("font-medium", getRoleBadgeClass(row.role))}>
          <Shield className="h-3 w-3 mr-1" />
          {getRoleName(row.role)}
        </Badge>
      )
    },
    {
      header: 'Phone',
      cell: (row) => row.phone || '—'
    },
    {
      header: 'Assigned Houses',
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.assigned_houses?.length || 0} houses
        </span>
      )
    },
    {
      header: 'Status',
      cell: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.is_active 
            ? 'bg-green-500/10 text-green-500' 
            : 'bg-red-500/10 text-red-500'
        }`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      header: 'Created',
      cell: (row) => formatDate(row.created_at)
    },
    {
      header: 'Actions',
      cell: (row) => (
        (row.role === 'worker' || row.role === 'house_manager') ? (
          <Button 
            variant="default" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/users/${row.id}`);
            }}
            data-testid={`view-user-profile-${row.id}`}
          >
            View Profile
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <PageHeader 
        title="User Management"
        description="Manage staff and family accounts"
        actions={
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input 
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                      data-testid="user-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input 
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                      data-testid="user-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    data-testid="user-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input 
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    data-testid="user-password"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <Select 
                      value={newUser.role} 
                      onValueChange={(v) => setNewUser({...newUser, role: v})}
                    >
                      <SelectTrigger data-testid="user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="house_manager">House Manager</SelectItem>
                        <SelectItem value="worker">Worker</SelectItem>
                        <SelectItem value="family">Family/Coordinator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input 
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                      data-testid="user-phone"
                    />
                  </div>
                </div>

                {['house_manager', 'worker'].includes(newUser.role) && (
                  <div className="space-y-2">
                    <Label>Assigned Houses</Label>
                    <Select 
                      value={newUser.assigned_houses[0] || ''} 
                      onValueChange={(v) => setNewUser({...newUser, assigned_houses: [v]})}
                    >
                      <SelectTrigger data-testid="user-houses">
                        <SelectValue placeholder="Select house" />
                      </SelectTrigger>
                      <SelectContent>
                        {houses.map(h => (
                          <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {newUser.role === 'family' && (
                  <div className="space-y-2">
                    <Label>Linked Participants</Label>
                    <Select 
                      value={newUser.linked_participants[0] || ''} 
                      onValueChange={(v) => setNewUser({...newUser, linked_participants: [v]})}
                    >
                      <SelectTrigger data-testid="user-participants">
                        <SelectValue placeholder="Select participant" />
                      </SelectTrigger>
                      <SelectContent>
                        {participants.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.first_name} {p.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-user-btn">Create User</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Filters */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48" data-testid="user-role-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="house_manager">House Manager</SelectItem>
                  <SelectItem value="worker">Worker</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 max-w-md">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="user-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <DataTable 
        columns={columns}
        data={filteredUsers}
        emptyMessage="No users found"
      />
    </div>
  );
};

export default UsersPage;
